<template>
  <div>
    <div class="d-flex flex-row">
      <p class="subtitle-2 font-weight-bold mb-4">Payment Requests</p>
      <div v-if="!mobile" class="flex-grow-1"></div>
      <div v-if="showBatchActions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              class="mx-1"
              small
              depressed
              color="white"
              :disabled="!createCombinedPaymentActivateCondition"
              v-on="on"
              @click="batchCreateCombinedPayment"
              >Create combined payment</v-btn
            >
          </template>
          <p class="white--text">
            Create a paid payment for the selected payment requests. Can only be
            used if the selection is from the same tenant.
          </p>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              class="mx-1"
              small
              depressed
              color="white"
              :disabled="
                !approveActivateCondition || !paymentrequestAccess.approve
              "
              v-on="on"
              @click="batchApprovePaymentRequests"
              >Approve
            </v-btn>
          </template>
          <p class="white--text">
            Approve multiple payment requests that are in pending approval
            state.
          </p>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              class="mx-1"
              small
              depressed
              color="white"
              :disabled="
                !approveActivateCondition || !paymentrequestAccess.reject
              "
              v-on="on"
              @click="batchRejectPaymentRequests"
              >Reject
            </v-btn>
          </template>
          <p class="white--text">
            Reject multiple payment requests that are in pending approval state.
          </p>
        </v-tooltip>
        <v-btn
          class="mx-1"
          small
          depressed
          color="white"
          :disabled="
            !voidAndCancelActivateCondition || !paymentrequestAccess.void
          "
          @click="batchVoidPaymentRequests"
          >Void</v-btn
        >
        <v-btn
          class="mx-1"
          small
          depressed
          color="white"
          :disabled="
            !voidAndCancelActivateCondition || !paymentrequestAccess.cancel
          "
          @click="batchCancelPaymentRequests"
          >Cancel</v-btn
        >
      </div>
      <dots-menu :menu-items="menuItems"></dots-menu>
    </div>
    <v-card
      v-if="paymentAccess.read"
      v-show="!loading"
      elevation="0"
      class="card-container px-8 py-4"
    >
      <div v-if="!mobile">
        <v-data-table
          v-model="selected"
          class="denser"
          :headers="headers"
          :items="payments"
          :options.sync="options"
          :server-items-length="total"
          item-key="refNo"
          :loading="loading"
          loading-text="Loading... Please wait"
          no-data-text="All Payment Requests will appear here."
          multi-sort
          :footer-props="{ 'items-per-page-options': [12, 24, 48, 96, 200] }"
          :show-select="showBatchActions"
          dense
        >
          <template v-slot:item="{ item, isSelected, select }">
            <tr
              v-long-press="(e) => onRowLongPress(item, e)"
              style="cursor: pointer"
              @click="showPayment(item)"
              @contextmenu.prevent="showActions(item, $event)"
            >
              <td v-if="showBatchActions">
                <v-checkbox
                  v-model="isSelected"
                  hide-details
                  dense
                  class="mt-0"
                  @change="select"
                  @click.stop
                ></v-checkbox>
              </td>
              <td>
                <p class="caption">{{ item.refNo }}</p>
              </td>
              <td>
                <p class="caption">{{ item.date }}</p>
              </td>
              <td v-if="type !== Types.UNIT" class="text-overflow">
                <a class="caption" @click="redirectBed(item)">{{
                  item.unitName
                }}</a>
              </td>
              <td
                v-if="type !== Types.UNIT && type !== Types.USER"
                class="text-overflow"
              >
                <p class="caption">{{ item.tenantName }}</p>
              </td>
              <td>
                <p class="caption">{{ item.category }}</p>
              </td>
              <td>
                <p class="caption">{{ item.period }}</p>
              </td>
              <td>
                <p class="caption">{{ item.dueDate }}</p>
              </td>
              <td>
                <p class="caption">{{ formatCurrency(item.total) }}</p>
              </td>
              <td>
                <p :class="statusClass(item.raw_status)">{{ item.status }}</p>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
      <div v-else>
        <!-- Payments Mobile View -->
        <v-data-table
          class="denser"
          :headers="headers"
          :items="payments"
          :options.sync="options"
          :server-items-length="total"
          item-key="refNo"
          :loading="loading"
          loading-text="Loading... Please wait"
          no-data-text="All Payments and Transactions will appear here."
          multi-sort
          :footer-props="{ 'items-per-page-options': [5] }"
          dense
          :expanded.sync="expandedPayments"
          disable-sort
        >
          <template v-slot:item="{ item }">
            <tr
              style="cursor: pointer"
              class="tr-head"
              @click="showPayment(item)"
            >
              <div class="d-flex flex-column py-2">
                <div class="d-flex flex-row">
                  <v-col class="pa-0">
                    <p class="font-weight-bold body-2">
                      {{ item.category }}
                    </p>
                  </v-col>
                  <v-col class="pa-0" align="end">
                    <p class="font-weight-bold body-2">
                      {{ formatCurrency(item.amountPaid) }}
                    </p>
                  </v-col>
                </div>
                <div class="d-flex flex-row">
                  <v-col class="pa-0">
                    <p class="caption font-weight-bold">TOTAL</p>
                  </v-col>
                  <v-col class="pa-0" align="end">
                    <p class="caption">{{ formatCurrency(item.total) }}</p>
                  </v-col>
                </div>
                <div class="d-flex flex-row">
                  <v-col class="pa-0">
                    <p class="caption">{{ item.tenantName }}</p>
                  </v-col>
                  <v-col class="pa-0" align="end">
                    <p class="caption">{{ item.bedName }}</p>
                  </v-col>
                </div>
                <div class="d-flex flex-row">
                  <v-col class="pa-0">
                    <p class="caption">
                      Status:
                      <span :class="statusClass(item.raw_status)">{{
                        item.status
                      }}</span>
                    </p>
                  </v-col>
                  <v-col class="pa-0" align="end">
                    <div v-if="item.raw_status">
                      <div
                        v-if="
                          item.raw_status !== PaymentStatus.PENDING_APPROVAL
                        "
                        class="d-flex flex-row justify-end"
                      >
                        <action-button
                          :activate-condition="
                            (item.raw_status === PaymentStatus.PENDING ||
                              item.raw_status === PaymentStatus.PAID_PARTIAL) &&
                            paidpaymentAccess.create
                          "
                          :disabled-condition="!paidpaymentAccess.create"
                          title="Payment Received"
                          :action="() => setPaid(item)"
                          icon="mdi-check-decagram"
                          color="primary"
                        ></action-button>
                        <action-button
                          :activate-condition="
                            item.raw_status === PaymentStatus.PENDING &&
                            paymentrequestAccess.update
                          "
                          :disabled-condition="
                            !paymentrequestAccess.update || item.readonly
                          "
                          title="Update Payment"
                          :action="() => updatePayment(item)"
                          icon="mdi-pencil"
                          color="primary"
                        ></action-button>
                        <action-button
                          :activate-condition="
                            item.raw_status != PaymentStatus.VOID &&
                            item.raw_status !=
                              PaymentStatus.VOID_CREDIT_BALANCE &&
                            item.raw_status != PaymentStatus.CANCELED &&
                            paymentrequestAccess.void
                          "
                          :disabled-condition="
                            !paymentrequestAccess.void || item.readonly
                          "
                          title="Void Payment"
                          :action="() => voidPayment(item)"
                          icon="mdi-cash-remove"
                        ></action-button>
                        <action-button
                          :activate-condition="
                            item.raw_status != PaymentStatus.VOID &&
                            item.raw_status !=
                              PaymentStatus.VOID_CREDIT_BALANCE &&
                            item.raw_status != PaymentStatus.CANCELED &&
                            paymentrequestAccess.cancel
                          "
                          :disabled-condition="
                            !paymentrequestAccess.cancel || item.readonly
                          "
                          title="Cancel Payment"
                          :action="() => cancelPayment(item)"
                          icon="mdi-close"
                          color="red"
                        ></action-button>
                      </div>
                      <div v-else class="d-flex flex-row justify-end">
                        <action-button
                          :activate-condition="true"
                          :disabled-condition="!paymentrequestAccess.approve"
                          title="Approve payment request"
                          :action="() => approvePaymentRequest(item)"
                          icon="mdi-check"
                          color="primary"
                        ></action-button>
                        <action-button
                          :activate-condition="true"
                          :disabled-condition="!paymentrequestAccess.reject"
                          title="Reject payment request"
                          :action="() => rejectPaymentRequest(item)"
                          icon="mdi-cancel"
                          color="error"
                        ></action-button>
                      </div>
                    </div>
                  </v-col>
                </div>
                <div class="d-flex flex-row">
                  <v-col class="pa-0">
                    <p class="caption">{{ item.period }}</p>
                  </v-col>
                </div>
              </div>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-skeleton-loader
      v-show="loading"
      ref="skeleton"
      type="table"
      loading
      class="mx-auto"
    ></v-skeleton-loader>
    <Invoice ref="invoice"></Invoice>
    <ConfirmPayment ref="confirmPayment"></ConfirmPayment>
    <VoidPayment ref="voidPayment"></VoidPayment>
    <CancelPayment ref="cancelPayment"></CancelPayment>
    <UpdatePaymentRequest ref="updatePaymentRequest"></UpdatePaymentRequest>
    <ApprovePaymentRequest ref="approvePaymentRequest"></ApprovePaymentRequest>
    <combined-payment-selector
      ref="combinedPaymentSelector"
    ></combined-payment-selector>
    <batch-approve-payment-requests
      ref="batchApprovePaymentRequests"
    ></batch-approve-payment-requests>
    <batch-reject-payment-requests
      ref="batchRejectPaymentRequests"
    ></batch-reject-payment-requests>
    <batch-void-payment-requests
      ref="batchVoidPaymentRequests"
    ></batch-void-payment-requests>
    <batch-cancel-payment-requests
      ref="batchCancelPaymentRequests"
    ></batch-cancel-payment-requests>
    <context-menu
      ref="contextMenu"
      no-items-message="No actions available"
    ></context-menu>
  </div>
</template>

<script>
import Invoice from '../../components/Invoice.vue'
import ConfirmPayment from '../../components/payment/ConfirmPayment.vue'
import VoidPayment from '../../components/payment/VoidPayment.vue'
import CancelPayment from '../../components/payment/CancelPayment.vue'
import UpdatePaymentRequest from '../../components/units/UpdatePaymentRequest.vue'
import ApprovePaymentRequest from '../../components/payment/ApprovePaymentRequest.vue'
import ActionButton from '../../components/ActionButton.vue'
import DotsMenu from '../../components/menu/DotsMenu.vue'
import ContextMenu from '../../components/menu/ContextMenu.vue'
import CombinedPaymentSelector from '../../components/payment/CombinedPaymentSelector.vue'
import BatchApprovePaymentRequests from '../../components/payment/BatchApprovePaymentRequests.vue'
import BatchRejectPaymentRequests from '../../components/payment/BatchRejectPaymentRequests.vue'
import BatchVoidPaymentRequests from '../../components/payment/BatchVoidPaymentRequests.vue'
import BatchCancelPaymentRequests from '../../components/payment/BatchCancelPaymentRequests.vue'

import Hashids from 'hashids'
import { nanoid } from 'nanoid/non-secure'
import { PaymentStatus, PaymentCategories } from '../../config/payment'
import { blockClick } from '../../util/longPressDirective'
const hashids = new Hashids()

export default {
  components: {
    Invoice,
    ConfirmPayment,
    VoidPayment,
    CancelPayment,
    UpdatePaymentRequest,
    ApprovePaymentRequest,
    ActionButton,
    DotsMenu,
    CombinedPaymentSelector,
    BatchApprovePaymentRequests,
    BatchRejectPaymentRequests,
    BatchVoidPaymentRequests,
    BatchCancelPaymentRequests,
    ContextMenu
  },
  props: [
    'searchString',
    'selectedTags',
    'selectedCategories',
    'selectedStatuses',
    'reloadTrigger',
    'tenantId',
    'type'
  ],
  data() {
    return {
      loading: true,
      limit: 0,
      total: 0,
      options: {},
      PaymentStatus,
      PaymentCategories,
      Types: {
        UNIT: 'unit',
        USER: 'user'
      },
      showBatchActions: false,
      showContextMenu: false,
      selected: []
    }
  },
  computed: {
    payments() {
      return this.$store.getters['payments/getPaymentRequests']
    },
    headers() {
      let headers = this.$store.getters['payments/getPaymentRequestHeaders']

      if (this.showBatchActions) {
        headers = headers.filter((h) => h.value !== 'actions')
      }

      switch (this.type) {
        case this.Types.UNIT:
          return headers.filter(
            (h) => !['unitName', 'tenantName'].includes(h.value)
          )
        case this.Types.USER:
          return headers.filter((h) => !['tenantName'].includes(h.value))
        default:
          return headers
      }
    },
    paymentAccess() {
      return this.$root.$roleAccess('payment').access
    },
    paymentrequestAccess() {
      return this.$root.$roleAccess('paymentrequest').access
    },
    paidpaymentAccess() {
      return this.$root.$roleAccess('paidpayment').access
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    },
    menuItems() {
      return [
        {
          icon: 'mdi-pencil',
          title: 'Toggle batch actions',
          action: this.toggleBatchActions,
          disabled: null,
          message: 'Batch actions on payment requests'
        }
      ]
    },
    createCombinedPaymentActivateCondition() {
      return (
        this.selected.length > 0 &&
        this.selected.every(
          (item) =>
            (item.raw_status === PaymentStatus.PENDING ||
              item.raw_status === PaymentStatus.PAID_PARTIAL) &&
            item.tenantId === this.selected[0].tenantId
        )
      )
    },
    approveActivateCondition() {
      return (
        this.selected.length > 0 &&
        this.selected.every(
          (item) => item.raw_status === PaymentStatus.PENDING_APPROVAL
        )
      )
    },
    voidAndCancelActivateCondition() {
      return (
        this.selected.length > 0 &&
        this.selected.every((item) => item.raw_status === PaymentStatus.PENDING)
      )
    }
  },
  watch: {
    options: {
      async handler() {
        await this.reload()
      }
    },
    reloadTrigger: {
      async handler() {
        await this.reload()
      }
    }
  },
  methods: {
    reloadFromAction() {
      this.$emit('reload')
      this.reload()
    },
    async reload() {
      this.loading = true
      const response = await this.$store.dispatch(
        'payments/fetchPaymentRequestsAction',
        {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy,
          sortDesc: this.options.sortDesc,
          search: this.searchString,
          status: this.selectedStatuses || [],
          categories: this.selectedCategories,
          tags: this.selectedTags,
          tenantId: this.tenantId
        }
      )

      this.loading = false
      this.limit = response.limit
      this.total = response.count
    },
    showPayment(item) {
      if (this.$refs.invoice) this.$refs.invoice.open({ item: item })
    },
    redirectBed(params) {
      const { bedId, username } = params
      if (bedId != null) this.$router.push(`/bed/${hashids.encode(bedId)}`)
      else this.$router.push(`/user/${username}`)
    },
    voidPayment(item) {
      this.$refs.voidPayment.open(item).then(() => {
        this.reloadFromAction()
      })
    },
    cancelPayment(item) {
      this.$refs.cancelPayment.open(item).then(() => {
        this.reloadFromAction()
      })
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    async updatePayment(item) {
      this.$refs.updatePaymentRequest.open(item).then(async () => {
        this.loading = true
        await this.reloadFromAction()
      })
    },
    statusClass(status) {
      if (
        [PaymentStatus.PENDING, PaymentStatus.PENDING_APPROVAL].includes(status)
      ) {
        return 'red--text caption'
      } else {
        return 'caption'
      }
    },
    paymentDetailItemAmountPaid(payment, paidPaymentId) {
      const items = payment.paidItems || []
      return (
        items
          .filter((item) => item.paidPaymentId === paidPaymentId)
          .reduce((a, b) => a + b.amount, 0) ||
        payment.amountPaid ||
        0
      )
    },
    generateId() {
      return nanoid(5)
    },
    async setPaid(item) {
      this.$refs.confirmPayment.open(item).then(() => {
        this.reloadFromAction()
      })
    },
    approvePaymentRequest(payment) {
      this.$refs.approvePaymentRequest.open(payment).then(() => {
        this.reloadFromAction()
      })
    },
    rejectPaymentRequest(paymentId) {
      this.$root
        .$confirm({
          title: 'Reject payment request',
          message: 'Are you sure you want to reject the payment request?'
        })
        .then(async (confirm) => {
          if (confirm) {
            await this.$store.dispatch('payments/rejectPaymentRequestAction', {
              paymentId
            })
            await this.reloadFromAction()
          }
        })
    },
    toggleBatchActions() {
      // show checkboxes on the left
      // hide actions
      // show batch action buttons
      this.showBatchActions = !this.showBatchActions
    },
    batchCreateCombinedPayment() {
      this.$refs.combinedPaymentSelector
        .open({ paymentRequests: this.selected })
        .then(() => {
          this.reloadFromAction()
          this.resetSelected()
        })
    },
    batchApprovePaymentRequests() {
      this.$refs.batchApprovePaymentRequests
        .open({ paymentRequests: this.selected })
        .then(() => {
          this.reloadFromAction()
          this.resetSelected()
        })
    },
    batchRejectPaymentRequests() {
      this.$refs.batchRejectPaymentRequests
        .open({ paymentRequests: this.selected })
        .then(() => {
          this.reloadFromAction()
          this.resetSelected()
        })
    },
    getBaseActions(item) {
      if (item.raw_status !== PaymentStatus.PENDING_APPROVAL) {
        return [
          {
            icon: 'mdi-check-decagram',
            color: 'primary',
            title: 'Payment Received',
            action: () => this.setPaid(item),
            message: 'Update the details of the payment request',
            show:
              (item.raw_status === PaymentStatus.PENDING ||
                item.raw_status === PaymentStatus.PAID_PARTIAL) &&
              this.paidpaymentAccess.create,
            disabled: !this.paidpaymentAccess.create
          },
          {
            icon: 'mdi-pencil',
            color: 'primary',
            title: 'Update Payment',
            action: () => this.updatePayment(item),
            message: 'Update the details of the payment request',
            show:
              item.raw_status === PaymentStatus.PENDING &&
              this.paymentrequestAccess.update,
            disabled: !this.paymentrequestAccess.update || item.readonly
          },
          {
            icon: 'mdi-cash-remove',
            title: 'Void Payment',
            action: () => this.voidPayment(item),
            message:
              "Void the payment and show in tenant's statement of account",
            show:
              item.raw_status === PaymentStatus.PENDING &&
              this.paymentrequestAccess.void,
            disabled: !this.paymentrequestAccess.void || item.readonly
          },
          {
            icon: 'mdi-close',
            color: 'error',
            title: 'Cancel Payment',
            action: () => this.cancelPayment(item),
            message:
              "Cancel the payment and hide from the tenant's statement of account",
            show:
              item.raw_status === PaymentStatus.PENDING &&
              this.paymentrequestAccess.cancel,
            disabled: !this.paymentrequestAccess.cancel || item.readonly
          }
        ]
      } else {
        return [
          {
            icon: 'mdi-check',
            color: 'primary',
            title: 'Approve payment request',
            action: () => this.approvePaymentRequest(item),
            message: 'Approve creation of a new payment request',
            show: true,
            disabled: !this.paymentrequestAccess.approve
          },
          {
            icon: 'mdi-cancel',
            color: 'error',
            title: 'Reject payment request',
            action: () => this.rejectPaymentRequest(item.paymentId),
            message: 'Reject creation of a new payment request',
            show: true,
            disabled: !this.paymentrequestAccess.reject
          }
        ]
      }
    },
    showActions(item, event) {
      const actions = this.getBaseActions(item)

      this.$refs.contextMenu.open({
        actions,
        x: event.clientX,
        y: event.clientY
      })

      this.$nextTick(() => {
        this.showContextMenu = true
      })
    },
    batchVoidPaymentRequests() {
      this.$refs.batchVoidPaymentRequests
        .open({ paymentRequests: this.selected })
        .then(() => {
          this.reloadFromAction()
          this.resetSelected()
        })
    },
    batchCancelPaymentRequests() {
      this.$refs.batchCancelPaymentRequests
        .open({ paymentRequests: this.selected })
        .then(() => {
          this.reloadFromAction()
          this.resetSelected()
        })
    },
    resetSelected() {
      this.selected = []
    },
    onRowLongPress(item, event) {
      this.showActions(item, event)
      blockClick(window)
    }
  }
}
</script>

<style scoped>
table thead tr th {
  white-space: nowrap !important;
}
.card-container {
  border-radius: 12px !important;
}
.v-input {
  border-radius: 10px;
}
table tbody tr td {
  /*border: none !important;
    border-bottom: none !important;*/
  border-color: #eeeeee !important;
}
p {
  margin-bottom: 0;
}
.text-overflow {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
tr.bottom-border td {
  /*border-bottom: thin solid #CCC !important;*/
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}
.table-footer-prepend {
  margin-top: -54px;
  height: 48px;
}
</style>
